import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import AuthService from "../service/AuthService";
import PluginService from "../service/PluginService";
import PluginI18nService from "../service/PluginI18nService";
import type {
	PluginInfo,
	ExposedRoute,
	ExposedRouteParam,
} from "../service/PluginService";

import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import FlexBox from "sap/m/FlexBox";
import VBox from "sap/m/VBox";
import Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import EventBus from "sap/ui/core/EventBus";
import FileUploader from "sap/ui/unified/FileUploader";
import Fragment from "sap/ui/core/Fragment";
import Dialog from "sap/m/Dialog";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import Label from "sap/m/Label";
import Input from "sap/m/Input";
import HBox from "sap/m/HBox";
import FlexItemData from "sap/m/FlexItemData";
import ObjectStatus from "sap/m/ObjectStatus";

const SECRET_SENTINEL = "***";

/**
 * A plugin plus its resolved display label. `name` stays the plain technical
 * name so admins still see what the plugin is called on disk and in audit logs;
 * `displayLabel` is what the card and dialogs render.
 */
type PluginListItem = PluginInfo & { displayLabel: string };

/**
 * @namespace com.ang.portal.controller
 */
export default class Plugins extends BaseController {
	private _wasDragged = false;
	private _draggingIndex = -1;
	private _configDialog: Dialog | null = null;
	private _configPlugin: PluginListItem | null = null;

	public onInit(): void {
		this.setModel(
			new JSONModel({
				items: [] as PluginListItem[],
				busy: false,
				actionsEnabled: true,
				canAdd: false,
				canUpdate: false,
				canDelete: false,
				exposedRoutes: [] as (ExposedRoute & { paramsHtml: string })[],
				exposedBusy: false,
			}),
			"pluginsPage",
		);

		this.refreshPermissions();
		this._attachCardClickDelegate();
	}

	public refreshPage(): void {
		this.refreshPermissions();
		if (AuthService.isAuthenticated()) {
			void this.loadPlugins();
			void this.loadExposedRoutes();
		}
	}

	private refreshPermissions(): void {
		const userInfo = AuthService.getUserInfo();
		const roles = userInfo?.roles ?? [];
		// Installing, updating and removing plugins is system:admin only. The old
		// plugin:add/update/delete permissions sat behind the same role gate and are gone.
		const isSystemAdmin = roles.includes("system:admin");

		const model = this.getModel("pluginsPage") as JSONModel;
		model.setProperty("/canAdd", isSystemAdmin);
		model.setProperty("/canUpdate", isSystemAdmin);
		model.setProperty("/canDelete", isSystemAdmin);
	}

	private async loadPlugins(): Promise<void> {
		const model = this.getModel("pluginsPage") as JSONModel;
		model.setProperty("/busy", true);

		try {
			const plugins = await PluginService.getPlugins();
			model.setProperty("/items", await this._withDisplayLabels(plugins));
		} catch {
			await this.showToast("pluginsLoadError");
		} finally {
			model.setProperty("/busy", false);
		}
	}

	/**
	 * Resolve each plugin's manifest label. `displayName` may be an "{i18n>key}"
	 * reference into the plugin's own bundle; a literal or missing value falls
	 * back to the plain technical name.
	 */
	private async _withDisplayLabels(
		plugins: PluginInfo[],
	): Promise<PluginListItem[]> {
		return Promise.all(
			plugins.map(async (plugin) => {
				const t = await PluginI18nService.getResolver(plugin.id, [
					plugin.displayName,
				]);
				return { ...plugin, displayLabel: t(plugin.displayName, plugin.name) };
			}),
		);
	}

	private async loadExposedRoutes(): Promise<void> {
		const model = this.getModel("pluginsPage") as JSONModel;
		model.setProperty("/exposedBusy", true);

		try {
			const routes = await PluginService.getExposedRoutes();
			model.setProperty(
				"/exposedRoutes",
				routes.map((route) => ({
					...route,
					paramsHtml: this._renderParamsHtml(route.params ?? []),
				})),
			);
		} catch {
			// Non-fatal: leave the catalog empty if it cannot be loaded.
			model.setProperty("/exposedRoutes", []);
		} finally {
			model.setProperty("/exposedBusy", false);
		}
	}

	/** Render a param list (with nested json fields/item) as an HTML tree for FormattedText. */
	private _renderParamsHtml(params: ExposedRouteParam[]): string {
		if (!params || params.length === 0) {
			return "—";
		}
		const esc = (s: string): string =>
			s
				.replace(/&/g, "&amp;")
				.replace(/</g, "&lt;")
				.replace(/>/g, "&gt;");

		const renderItem = (p: ExposedRouteParam): string => {
			let typeLabel = esc(p.type);
			let nested = "";
			if (p.type === "json") {
				if (p.fields && p.fields.length > 0) {
					nested = `<ul>${p.fields.map(renderItem).join("")}</ul>`;
				} else if (p.item) {
					typeLabel = p.item.fields
						? "json[]"
						: `${esc(p.item.type ?? "json")}[]`;
					if (p.item.fields && p.item.fields.length > 0) {
						nested = `<ul>${p.item.fields.map(renderItem).join("")}</ul>`;
					}
				}
			}
			const req = p.required ? "*" : "";
			return `<li><strong>${esc(p.name)}${req}</strong>: ${typeLabel}${nested}</li>`;
		};

		return `<ul>${params.map(renderItem).join("")}</ul>`;
	}

	public async onPluginFileSelected(oEvent: Event): Promise<void> {
		const files = oEvent.getParameter("files" as never) as FileList;
		const file = files?.[0];
		if (!file) {
			return;
		}

		const model = this.getModel("pluginsPage") as JSONModel;
		model.setProperty("/busy", true);

		try {
			await this._uploadPlugin(file, false);
		} finally {
			// Reset the FileUploader so re-selecting the same filename re-fires `change`.
			const uploader = this.byId("pluginUploadButton") as
				| FileUploader
				| undefined;
			uploader?.clear();
			EventBus.getInstance().publish("PluginsChannel", "PluginsChanged");
			model.setProperty("/busy", false);
		}
	}

	private async _uploadPlugin(file: File, update: boolean): Promise<void> {
		try {
			await PluginService.uploadPlugin(file, { update });
			// An update can ship new or changed texts, so drop the cached bundles.
			PluginI18nService.clear();
			await this.loadPlugins();
			await this.loadExposedRoutes();
			await this.showToast(update ? "pluginsUpdateSuccess" : "pluginsUploadSuccess");
		} catch (error) {
			const err = error as Error & {
				status?: number;
				code?: string;
				pluginName?: string;
				currentVersion?: string;
				newVersion?: string;
			};
			// Plugin already installed: offer to replace it, keeping its saved configuration.
			if (!update && err.status === 409 && err.code === "PLUGIN_EXISTS") {
				const confirmed = await this._confirmUpdate(err);
				if (confirmed) {
					await this._uploadPlugin(file, true);
				}
				return;
			}
			await this.showToastForError(
				error,
				update ? "pluginsUpdateError" : "pluginsUploadError",
			);
		}
	}

	private async _confirmUpdate(err: {
		pluginName?: string;
		currentVersion?: string;
		newVersion?: string;
	}): Promise<boolean> {
		const bundle = await this.getResourceBundle();
		return new Promise<boolean>((resolve) => {
			MessageBox.confirm(
				bundle.getText("pluginsUpdateConfirm", [
					err.pluginName ?? "",
					err.currentVersion ?? "",
					err.newVersion ?? "",
				]),
				{
					title: bundle.getText("pluginsUpdateTitle"),
					actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
					emphasizedAction: MessageBox.Action.OK,
					onClose: (action: string) => resolve(action === "OK"),
				},
			);
		});
	}

	private getPluginFromEvent(oEvent: Event): PluginListItem | null {
		const source = oEvent.getSource();
		if (!(source instanceof Control)) {
			return null;
		}
		return source
			.getBindingContext("pluginsPage")
			?.getObject() as PluginListItem | null;
	}

	public onPluginCardPress(oEvent: Event): void {
		const plugin = this.getPluginFromEvent(oEvent);
		if (!plugin) {
			return;
		}
		this.getRouter().navTo("homePlugin", { pluginId: plugin.id });
	}

	private _attachCardClickDelegate(): void {
		const container = this.byId("pluginsTileContainer") as FlexBox | undefined;
		if (!container) {
			return;
		}

		container.addEventDelegate(
			{
				onAfterRendering: () => {
					// Make each card draggable via native HTML5 DnD
					container
						.getDomRef()
						?.querySelectorAll<HTMLElement>(".pluginCard")
						.forEach((el) => {
							el.draggable = true;
						});
				},
				ondragstart: (e: DragEvent) => {
					const card = (e.target as HTMLElement).closest<HTMLElement>(".pluginCard");
					if (!card) return;
					this._wasDragged = true;
					this._draggingIndex = this._cardIndex(card, container);
					if (e.dataTransfer) {
						e.dataTransfer.effectAllowed = "move";
						e.dataTransfer.setDragImage(card, e.offsetX, e.offsetY);
					}
				},
				ondragover: (e: DragEvent) => {
					e.preventDefault();
					if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
				},
				ondrop: (e: DragEvent) => {
					e.preventDefault();
					const card = (e.target as HTMLElement).closest<HTMLElement>(".pluginCard");
					if (!card) return;
					const toIndex = this._cardIndex(card, container);
					if (
						toIndex === -1 ||
						this._draggingIndex === -1 ||
						toIndex === this._draggingIndex
					) {
						return;
					}
					void this._reorderCards(this._draggingIndex, toIndex);
					this._draggingIndex = -1;
				},
				ondragend: () => {
					this._draggingIndex = -1;
				},
				onclick: (oEvent: { target: HTMLElement }) => {
					// Suppress the click that browsers fire after a completed drag
					if (this._wasDragged) {
						this._wasDragged = false;
						return;
					}
					const target = oEvent.target as HTMLElement;
					// Ignore clicks on action buttons
					if (target.closest(".pluginCardActions")) {
						return;
					}
					const cardDom = target.closest<HTMLElement>(".pluginCard");
					if (!cardDom) {
						return;
					}
					const items = container.getItems();
					const cardControl = items.find(
						(item) =>
							item.getDomRef() === cardDom || cardDom.contains(item.getDomRef()),
					);
					if (!cardControl) {
						return;
					}
					const plugin = cardControl
						.getBindingContext("pluginsPage")
						?.getObject() as PluginListItem | null;
					if (plugin) {
						this.getRouter().navTo("homePlugin", { pluginId: plugin.id });
					}
				},
			},
			this,
		);
	}

	private _cardIndex(cardDom: HTMLElement, container: FlexBox): number {
		return container
			.getItems()
			.findIndex(
				(item) => item.getDomRef() === cardDom || cardDom.contains(item.getDomRef()),
			);
	}

	private async _reorderCards(fromIndex: number, toIndex: number): Promise<void> {
		const model = this.getModel("pluginsPage") as JSONModel;
		const items = (model.getProperty("/items") as PluginListItem[]).slice();
		const [moved] = items.splice(fromIndex, 1);
		items.splice(toIndex, 0, moved);
		model.setProperty("/items", items);
		try {
			await PluginService.reorderPlugins(items.map((p) => p.id));
			EventBus.getInstance().publish("PluginsChannel", "PluginsChanged");
		} catch {
			await this.showToast("pluginsDragReorderError");
			void this.loadPlugins();
		}
	}

	public async onTogglePluginPress(oEvent: Event): Promise<void> {
		const plugin = this.getPluginFromEvent(oEvent);
		if (!plugin) {
			return;
		}

		// Disabling unloads the plugin for every user, so require an explicit confirmation.
		// Enabling is harmless and stays a single click.
		if (plugin.enabled) {
			const bundle = await this.getResourceBundle();
			const confirmed = await new Promise<boolean>((resolve) => {
				MessageBox.confirm(bundle.getText("pluginsDisableConfirm", [plugin.displayLabel]), {
					title: bundle.getText("pluginsDisableTitle"),
					actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
					emphasizedAction: MessageBox.Action.CANCEL,
					onClose: (action: string) => resolve(action === "OK"),
				});
			});

			if (!confirmed) {
				return;
			}
		}

		const model = this.getModel("pluginsPage") as JSONModel;
		model.setProperty("/busy", true);

		try {
			if (plugin.enabled) {
				await PluginService.disablePlugin(plugin.id);
			} else {
				await PluginService.enablePlugin(plugin.id);
			}
			await this.loadPlugins();
			await this.loadExposedRoutes();
			EventBus.getInstance().publish("PluginsChannel", "PluginsChanged");
		} catch {
			await this.showToast("pluginsToggleError");
		} finally {
			model.setProperty("/busy", false);
		}
	}

	public async onPluginConfigPress(oEvent: Event): Promise<void> {
		const plugin = this.getPluginFromEvent(oEvent);
		if (!plugin || !plugin.envVars || plugin.envVars.length === 0) {
			return;
		}

		this._configPlugin = plugin;

		if (!this._configDialog) {
			this._configDialog = (await Fragment.load({
				id: this.getView()?.getId(),
				name: "com.ang.portal.fragment.PluginConfigDialog",
				controller: this,
			})) as Dialog;
			this.getView()?.addDependent(this._configDialog);
		}

		const dialogModel = new JSONModel({
			title: plugin.displayLabel,
			config: { ...plugin.config },
		});
		this._configDialog.setModel(dialogModel, "pluginConfigDialog");

		const formContainer = Fragment.byId(
			this.getView()?.getId() ?? "",
			"pluginConfigFormContainer",
		) as VBox | undefined;

		if (formContainer) {
			formContainer.destroyItems();
			const bundle = await this.getResourceBundle();
			// Env var labels may be "{i18n>key}" references into the plugin's own bundle.
			const t = await PluginI18nService.getResolver(
				plugin.id,
				plugin.envVars.map((envVar) => envVar.label),
			);
			const formContent: (Label | HBox | Input)[] = [];
			for (const envVar of plugin.envVars) {
				formContent.push(
					new Label({
						text: t(envVar.label, envVar.key),
						required: !!envVar.required,
					}),
				);

				const input = new Input({
					value: `{pluginConfigDialog>/config/${envVar.key}}`,
					type: envVar.secret ? "Password" : "Text",
					placeholder: envVar.secret
						? bundle.getText("pluginsConfigSecretPlaceholder")
						: envVar.default || "",
					layoutData: new FlexItemData({ growFactor: 1 }),
				});

				if (envVar.shared) {
					formContent.push(
						new HBox({
							alignItems: "Center",
							items: [
								input,
								new ObjectStatus({
									text: bundle.getText("pluginsConfigSharedBadge"),
									icon: "sap-icon://share-2",
									state: "Information",
								}).addStyleClass("sapUiSmallMarginBegin"),
							],
						}),
					);
				} else {
					formContent.push(input);
				}
			}
			const form = new SimpleForm({
				editable: true,
				layout: "ResponsiveGridLayout",
				labelSpanXL: 4,
				labelSpanL: 4,
				labelSpanM: 4,
				labelSpanS: 12,
				emptySpanXL: 0,
				emptySpanL: 0,
				emptySpanM: 0,
				emptySpanS: 0,
				columnsXL: 1,
				columnsL: 1,
				columnsM: 1,
				content: formContent,
			});
			formContainer.addItem(form);
		}

		this._configDialog.open();
	}

	public async onPluginConfigSave(): Promise<void> {
		if (!this._configPlugin || !this._configDialog) {
			return;
		}

		const dialogModel = this._configDialog.getModel("pluginConfigDialog") as JSONModel;
		const config = dialogModel.getProperty("/config") as Record<string, string>;

		// For required fields that are blank, fall back to the declared default if one exists.
		for (const ev of this._configPlugin.envVars) {
			if (ev.required && !config[ev.key]?.trim() && ev.default) {
				config[ev.key] = ev.default;
				dialogModel.setProperty(`/config/${ev.key}`, ev.default);
			}
		}

		const missingRequired = this._configPlugin.envVars.filter(
			(ev) =>
				ev.required &&
				config[ev.key] !== SECRET_SENTINEL &&
				!config[ev.key]?.trim(),
		);
		if (missingRequired.length > 0) {
			const bundle = await this.getResourceBundle();
			const t = await PluginI18nService.getResolver(
				this._configPlugin.id,
				missingRequired.map((ev) => ev.label),
			);
			MessageToast.show(
				bundle.getText("pluginsConfigRequiredError", [
					missingRequired.map((ev) => t(ev.label, ev.key)).join(", "),
				]),
			);
			return;
		}

		try {
			await PluginService.updatePluginConfig(this._configPlugin.id, config);
			// Reflect saved values back into the plugins list model, keeping existing
			// secret values intact (they stay as "***" from the API response).
			const model = this.getModel("pluginsPage") as JSONModel;
			const items = model.getProperty("/items") as PluginListItem[];
			const idx = items.findIndex((p) => p.id === this._configPlugin!.id);
			if (idx !== -1) {
				const existing = (items[idx].config ?? {}) as Record<string, string>;
				const merged: Record<string, string> = { ...existing };
				for (const [k, v] of Object.entries(config)) {
					if (v !== SECRET_SENTINEL) {
						merged[k] = v;
					}
				}
				model.setProperty(`/items/${idx}/config`, merged);
			}
			const bundle = await this.getResourceBundle();
			MessageToast.show(bundle.getText("settingsPluginSaveSuccess"));
			this._configDialog.close();
		} catch {
			const bundle = await this.getResourceBundle();
			MessageToast.show(bundle.getText("settingsPluginSaveError"));
		}
	}

	public onPluginConfigCancel(): void {
		this._configDialog?.close();
	}

	public async onRemovePluginPress(oEvent: Event): Promise<void> {
		const plugin = this.getPluginFromEvent(oEvent);
		if (!plugin) {
			return;
		}

		const bundle = await this.getResourceBundle();
		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(
				bundle.getText("pluginsDeleteConfirm", [plugin.displayLabel]),
				{
					actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
					emphasizedAction: MessageBox.Action.CANCEL,
					onClose: (action: string) => resolve(action === "OK"),
				},
			);
		});

		if (!confirmed) {
			return;
		}

		const model = this.getModel("pluginsPage") as JSONModel;
		model.setProperty("/busy", true);

		try {
			await PluginService.removePlugin(plugin.id);
			PluginI18nService.clear(plugin.id);
			await this.showToast("pluginsDeleteSuccess");
			await this.loadPlugins();
			await this.loadExposedRoutes();
			EventBus.getInstance().publish("PluginsChannel", "PluginsChanged");
		} catch {
			await this.showToast("pluginsDeleteError");
		} finally {
			model.setProperty("/busy", false);
		}
	}
}
